import React, { useCallback, useState } from 'react';
import type { FieldPath } from 'react-hook-form';
import { Button } from '@packages/ui/primitives/Button';
import { palette } from '@packages/ui/tokens';
import type { FormEachNode } from '../../../types/layout';
import type { Compute } from '../../../types/compute';
import { useFormEngineForm, useFormEngineState } from '../../../context/FormEngineContext';
import { evaluateCompute } from '../../../evaluators/computeEvaluator';
import { evaluateCondition } from '../../../evaluators/conditionEvaluator';
import type { ConditionContext } from '../../../evaluators/conditionEvaluator';
import { ActionRegistry } from '@packages/table-builder/registry';

interface FormEachResetButtonProps {
  node: FormEachNode;
  index: number;
  /** Pass the current array length so React.memo re-renders when rows are added/removed — required for visibleWhen conditions that reference array.length. */
  arrayLength?: number;
}

export const FormEachResetButton = React.memo(function FormEachResetButton({
  node,
  index,
  arrayLength: _arrayLength,
}: FormEachResetButtonProps): React.ReactElement | null {
  const config = node.resetButton;
  const { form } = useFormEngineForm();
  const { meta, getOptionProp, isReadOnly } = useFormEngineState();
  const [isLoading, setIsLoading] = useState(false);
  const rowPrefix = `${node.name}.${index}`;

  const handleReset = useCallback(async () => {
    if (config == null) return;
    const currentValues = form.getValues() as Record<string, unknown>;
    const condCtx: ConditionContext = { values: currentValues, meta, getOptionProp };

    if (config.resetWhen && !evaluateCondition(config.resetWhen, condCtx)) {
      return;
    }

    if (config.defaults) {
      for (const [field, compute] of Object.entries(config.defaults)) {
        const cleaned = stripCaretPrefix(compute);
        const result = evaluateCompute(cleaned, {
          fieldValue: null,
          values: currentValues,
          meta,
          getOptionProp,
        });
        form.setValue(
          `${rowPrefix}.${field}` as FieldPath<Record<string, unknown>>,
          result as never,
          { shouldDirty: true, shouldValidate: false },
        );
      }
      return;
    }

    if (config.actionId) {
      setIsLoading(true);
      try {
        const resolvedParams: Record<string, unknown> = {};
        if (config.params) {
          for (const [key, path] of Object.entries(config.params)) {
            if (path === '*') {
              resolvedParams[key] = currentValues;
            } else if (path === '{rowIndex}') {
              resolvedParams[key] = index;
            } else {
              resolvedParams[key] = getNestedValue(currentValues, path.replace('{rowPrefix}', rowPrefix));
            }
          }
        }
        const result = await ActionRegistry.executeData(config.actionId, resolvedParams as Record<string, string>);
        if (result && typeof result === 'object') {
          for (const [field, value] of Object.entries(result as Record<string, unknown>)) {
            form.setValue(
              `${rowPrefix}.${field}` as FieldPath<Record<string, unknown>>,
              value as never,
              { shouldDirty: true, shouldValidate: false },
            );
          }
        }
      } finally {
        setIsLoading(false);
      }
    }
  }, [form, config, rowPrefix, index, meta, getOptionProp]);

  if (!config) return null;
  if (isReadOnly) return null;

  if (config.visibleWhen) {
    const currentValues = form.getValues() as Record<string, unknown>;
    const condCtx: ConditionContext = { values: currentValues, meta, getOptionProp };
    if (!evaluateCondition(config.visibleWhen, condCtx)) return null;
  }

  const isIconOnly = !config.label;

  return (
    <Button
      variant='ghost'
      size='md'
      onClick={handleReset}
      loading={isLoading}
      icon={config.icon ?? 'restart_alt'}
      label={isIconOnly ? undefined : config.label}
      type='button'
      semanticId={`${node.name}.${index}.reset`}
      style={{ color: palette.blue[600] }}
    />
  );
});

/**
 * Strips `^` prefix from all string values in a Compute expression tree.
 * Reset button evaluates against root form values, so `^` markers are unnecessary
 * and would cause field lookup failures.
 */
function stripCaretPrefix(compute: unknown): Compute {
  const raw = JSON.stringify(compute);
  if (!raw.includes('"^')) return compute as Compute;
  return JSON.parse(raw.replaceAll('"^', '"')) as Compute;
}

function getNestedValue(obj: Record<string, unknown>, path: string): unknown {
  return path.split('.').reduce<unknown>((acc, key) => {
    if (acc == null || typeof acc !== 'object') return undefined;
    return (acc as Record<string, unknown>)[key];
  }, obj);
}
